import { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { useCreateCashTransfer } from "hooks/useCreateCashTransfer";
import { useMutation } from "hooks/useMutation";
import { Loader } from "components/Loader";
import { TransactionModalContainer } from "./TransactionModal.styles";
import PlaidConnectionModal from "../PlaidConnectionModal";
import { UpdateBankAccountConfirmationModal } from "../UpdateBankAccountConfirmationModal";
import { usePlaid } from "hooks/usePlaid";
import { createACHLink } from "features/SurmountAccount/service";
import { toast } from "Layout/slice";
import { parseErrorMessage } from "utils";

const TransactionModal = ({
  show,
  onHide,
  onNext,
  accountDetails: propAccountDetails,
  type = "deposit", // 'deposit' or 'withdraw'
}) => {
  const dispatch = useDispatch();
  const { accountDetails: hookAccountDetails, isLoadingAccountDetails } =
    useCreateCashTransfer();

  // Use prop accountDetails if provided, otherwise use hook data
  const accountDetails = propAccountDetails || hookAccountDetails;

  const [amount, setAmount] = useState("");
  const [showPlaidModal, setShowPlaidModal] = useState(false);
  const [showUpdateConfirmation, setShowUpdateConfirmation] = useState(false);
  const [plaidActive, setPlaidActive] = useState(false);
  const [amountError, setAmountError] = useState("");

  // Create ACH Link Mutation for updating bank connection
  const createACHMutation = useMutation({
    mutationFn: ({ public_token, accountId }) =>
      createACHLink({ public_token, accountId }),
    onSuccess: () => {
      dispatch(
        toast({
          type: "success",
          body: "Bank account updated successfully!",
        })
      );
      setPlaidActive(false);
    },
    onError: (error) => {
      dispatch(
        toast({
          type: "error",
          body:
            parseErrorMessage(error) ||
            "Failed to update bank account. Please try again.",
        })
      );
      setPlaidActive(false);
    },
  });

  const isLoading = plaidActive || createACHMutation.isLoading;

  // Define callback functions
  const handlePlaidExit = useCallback(() => {
    setPlaidActive(false);
  }, []);

  const handlePlaidSuccess = useCallback(
    (public_token, metadata) => {
      createACHMutation.mutate({
        public_token,
        accountId: metadata.account_id,
      });
    },
    [createACHMutation]
  );

  // Initialize Plaid hook
  const { open, ready } = usePlaid({
    active: plaidActive,
    successCallback: handlePlaidSuccess,
    exitCallback: handlePlaidExit,
  });

  // Effect to open Plaid when ready
  useEffect(() => {
    if (ready && plaidActive) {
      open(); // Open Plaid Link
    }
  }, [ready, plaidActive, open]);

  const handlePlaidModalClose = useCallback(() => {
    setShowPlaidModal(false);
    setPlaidActive(false);
    onHide?.();
  }, [onHide]);

  const handlePlaidConnect = useCallback(() => {
    setShowPlaidModal(false);
    setPlaidActive(true);
    onHide?.();
  }, [onHide]);

  const handleUpdateBankAccount = useCallback(() => {
    setShowUpdateConfirmation(true);
  }, []);

  const handleUpdateConfirmationHide = useCallback(() => {
    setShowUpdateConfirmation(false);
  }, []);

  const handleUpdateConfirmationNext = useCallback(() => {
    setShowUpdateConfirmation(false);
    setShowPlaidModal(true);
    createACHMutation.reset();
  }, [createACHMutation]);

  // Extract bank and account details
  const { bankDetails, availableBalance } = useMemo(() => {
    if (!accountDetails) {
      return {
        bankDetails: null,
        availableBalance: "0.00",
      };
    }

    const alpacaAccount = accountDetails.account_details?.find(
      (account) => account.type === "alpaca"
    );
    const bankAccount = accountDetails.account_details?.find(
      (account) => account.type === "plaid_bank"
    );

    return {
      bankDetails: {
        name: bankAccount?.name || "--",
        institutionName: bankAccount?.institution_name || "--",
        accountType:
          bankAccount?.subtype?.charAt(0).toUpperCase() +
            bankAccount?.subtype?.slice(1) || "--",
        accountNumber: bankAccount?.mask || "--",
      },
      availableBalance: alpacaAccount?.available_balance || "0.00",
    };
  }, [accountDetails]);

  const handleAmountChange = useCallback(
    (e) => {
      let value = e.target.value.trim();
      if (/^\d*\.?\d*$/.test(value)) {
        // Prevent entering more than 2 decimals
        const [, dec] = value.split(".");
        if (!dec || dec.length <= 2) {
          setAmount(value);

          // Clear error when user is typing
          if (amountError) {
            setAmountError("");
          }

          // Validate for withdrawal
          if (type === "withdraw" && value) {
            const numericAmount = parseFloat(value);
            const numericBalance = parseFloat(availableBalance);
            if (!isNaN(numericAmount) && numericAmount > numericBalance) {
              setAmountError(
                `Amount exceeds available balance of $${numericBalance.toLocaleString(
                  "en-US",
                  {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }
                )}`
              );
            }
          }
        }
      }
    },
    [amountError, type, availableBalance]
  );

  const handleNext = useCallback(() => {
    const numericAmount = parseFloat(amount);
    const finalAmount = type === "withdraw" ? -numericAmount : numericAmount;

    onNext?.({
      amount: finalAmount,
      bankDetails,
      ...(type === "withdraw" && { availableBalance }),
    });
  }, [onNext, amount, bankDetails, availableBalance, type]);

  const isNextDisabled = useMemo(() => {
    const numericAmount = parseFloat(amount);
    const numericBalance = parseFloat(availableBalance);

    // Basic validation: amount must be valid and greater than 0
    if (!amount || isNaN(numericAmount) || numericAmount <= 0) {
      return true;
    }

    // For withdrawals: check if amount exceeds available balance
    if (type === "withdraw" && numericAmount > numericBalance) {
      return true;
    }

    return false;
  }, [amount, availableBalance, type]);

  // Dynamic content based on type
  const { modalTitle, inputLabel, inputPlaceholder } = useMemo(() => {
    const formattedBalance = parseFloat(availableBalance).toLocaleString(
      "en-US",
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
    );

    return type === "deposit"
      ? {
          modalTitle: "Deposit funds",
          inputLabel: "Deposit amount",
          inputPlaceholder: "Enter amount",
        }
      : {
          modalTitle: "Withdraw from Surmount",
          inputLabel: "Withdraw amount",
          inputPlaceholder: `max: ${formattedBalance}`,
        };
  }, [type, availableBalance]);

  return (
    <>
      <TransactionModalContainer
        show={show && !showPlaidModal && !showUpdateConfirmation}
        onHide={onHide}
        type={type}
      >
        <div className="modal-header">
          <h2 className="modal-title">{modalTitle}</h2>
        </div>

        <div className="modal-body-content">
          {isLoadingAccountDetails ? (
            <div className="loader-wrapper">
              <Loader />
            </div>
          ) : (
            <>
              {/* Available Balance Section (only for withdraw) */}
              {type === "withdraw" && (
                <div className="balance-section">
                  <div className="balance-label">
                    Available balance to withdraw
                  </div>
                  <h3 className="balance-amount">
                    $
                    {parseFloat(availableBalance).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{" "}
                    USD
                  </h3>
                </div>
              )}

              {/* Amount Input */}
              <div className="transaction-input-section">
                <label className="input-label">{inputLabel}</label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    className={`amount-input ${amountError ? "error" : ""}`}
                    placeholder={inputPlaceholder}
                    value={amount}
                    onChange={handleAmountChange}
                  />
                  <div className="currency-label">USD</div>
                </div>
                {amountError && (
                  <div className="error-message">{amountError}</div>
                )}
              </div>

              {/* Bank Details Section */}
              <div className="bank-details-section">
                <div className="section-header">
                  <h4 className="section-title">Bank details</h4>
                  <button
                    className="update-button"
                    onClick={handleUpdateBankAccount}
                    disabled={isLoading}
                  >
                    {isLoading ? "Updating..." : "Update"}
                  </button>
                </div>

                <div className="bank-details-content">
                  <div className="bank-detail-row">
                    <div className="detail-label">Name</div>
                    <div className="detail-value">{bankDetails?.name}</div>
                  </div>

                  <div className="bank-detail-row">
                    <div className="detail-label">Institution name</div>
                    <div className="detail-value">
                      {bankDetails?.institutionName}
                    </div>
                  </div>

                  <div className="bank-detail-row">
                    <div className="detail-label">Account type</div>
                    <div className="detail-value">
                      {bankDetails?.accountType}
                    </div>
                  </div>

                  <div className="bank-detail-row">
                    <div className="detail-label">Account number</div>
                    <div className="detail-value">
                      {bankDetails?.accountNumber}
                    </div>
                  </div>
                </div>
              </div>

              {/* Next Button */}
              <button
                className="action-button"
                onClick={handleNext}
                disabled={isNextDisabled}
              >
                Next
              </button>
            </>
          )}
        </div>
      </TransactionModalContainer>

      <UpdateBankAccountConfirmationModal
        show={showUpdateConfirmation}
        onHide={handleUpdateConfirmationHide}
        onConfirm={handleUpdateConfirmationNext}
      />

      <PlaidConnectionModal
        show={showPlaidModal}
        onHide={handlePlaidModalClose}
        onConnect={handlePlaidConnect}
        isLoading={false}
      />
    </>
  );
};

TransactionModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  onNext: PropTypes.func,
  accountDetails: PropTypes.object,
  type: PropTypes.oneOf(["deposit", "withdraw"]).isRequired,
};

export { TransactionModal };
